import React from 'react';
import { Modal, Typography, Space, Tag } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { HistoryChart } from '@/features/chart/components/HistoryChart';
import type { OperationPeriod } from '../types/operation-period.types';
import type { DataTypeKey } from '@/features/dashboard/types/dashboard.types';

const { Text, Title } = Typography;

// ========== 定数定義 ==========
const MODAL_CONSTANTS = {
  MODAL_WIDTH_DESKTOP: 1200,
  MODAL_WIDTH_MOBILE: '95%',
  TITLE_LEVEL: 5,
  MINUTES_PER_HOUR: 60,
} as const;

// デフォルトで表示するデータタイプ
const DEFAULT_DATA_TYPES: DataTypeKey[] = [
  'l_af_supply_volume',
  'h_af_supply_volume',
  'concrete_supply_volume',
  'l_h_ratio',
  'l_af_supply_volume_integral',
  'h_af_supply_volume_integral',
  'concrete_supply_volume_integral',
  'l_af_concrete_ratio',
  'h_af_concrete_ratio',
];

// ========== 型定義 ==========
interface OperationChartModalProps {
  readonly operationPeriod: OperationPeriod | null;
  readonly open: boolean;
  readonly isMobile: boolean;
  readonly onClose: () => void;
}

/**
 * 日時をフォーマット
 */
function formatDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');

  return `${year}/${month}/${day} ${hour}:${minute}`;
}

/**
 * 稼働時間をフォーマット
 */
function formatDuration(durationMinutes: number | null): string {
  if (durationMinutes === null) {
    return '-';
  }

  const hours = Math.floor(durationMinutes / MODAL_CONSTANTS.MINUTES_PER_HOUR);
  const minutes = durationMinutes % MODAL_CONSTANTS.MINUTES_PER_HOUR;

  if (hours > 0) {
    return `${hours}時間${minutes}分`;
  }

  return `${minutes}分`;
}

/**
 * 運転期間チャートモーダルコンポーネント
 */
export const OperationChartModal: React.FC<OperationChartModalProps> = ({
  operationPeriod,
  open,
  isMobile,
  onClose,
}) => {
  if (operationPeriod === null) {
    return null;
  }

  // 終了時刻がない場合（アクティブな場合）は現在時刻を使用
  const endDate = operationPeriod.endedAt ?? new Date();

  return (
    <Modal
      title={
        <Space>
          <Title level={MODAL_CONSTANTS.TITLE_LEVEL} style={{ margin: 0 }}>
            {operationPeriod.operationName}
          </Title>
          {operationPeriod.isActive && <Tag color="green">稼働中</Tag>}
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMobile ? MODAL_CONSTANTS.MODAL_WIDTH_MOBILE : MODAL_CONSTANTS.MODAL_WIDTH_DESKTOP}
      destroyOnClose
    >
      <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 16 }}>
        <Text type="secondary">
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {formatDateTime(operationPeriod.startedAt)} ～{' '}
          {operationPeriod.endedAt !== null
            ? formatDateTime(operationPeriod.endedAt)
            : '稼働中'}
        </Text>
        <Text type="secondary">
          稼働時間: {formatDuration(operationPeriod.durationMinutes)}
        </Text>
      </Space>

      <HistoryChart
        panelId={operationPeriod.panelId}
        selectedDataTypes={DEFAULT_DATA_TYPES}
        startDate={operationPeriod.startedAt}
        endDate={endDate}
      />
    </Modal>
  );
};
